import React, { useEffect, useRef, useState } from "react";

import {
  MinusCircleIcon,
  PlusCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { getBroadcastGroupsData } from "@services/API/Requests/BroadcastGroupRequests";
import LoadingIcon from "@common/LoadingIcon";
import useClickOutside from "./ClickOutsideHandler";
import { error_message } from "@constants/Errors";
import { BROADCASTGROUP } from "@types/broadcastGroup";

interface BroadcastGroupParentModalProps {
  isOpen: boolean;
  nodeId: number;
  onRequestClose: () => void;
  onSelectGroup: (originalId: number, parentId: number) => void;
}

const ChangeBroadcastGroupParentModal: React.FC<
  BroadcastGroupParentModalProps
> = ({ isOpen, nodeId, onRequestClose, onSelectGroup }) => {
  const [broadcastGroups, setBroadcastGroups] = useState<BROADCASTGROUP[]>([]);
  const [originalTenantDepartments, setOriginalTenantDepartments] = useState(
    [],
  );
  const [error, setError] = useState<String>("");
  const [loading, setLoading] = useState<boolean>(true);

  const modalRef = useRef<HTMLDivElement>(null);

  useClickOutside(isOpen, onRequestClose, modalRef);

  const returnId = (originalId, parentId) => {
    onSelectGroup(originalId, parentId);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await getBroadcastGroupsData(setBroadcastGroups, setLoading, setError);
      } catch (err) {
        setError(error_message.broadcast_group.not_found);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const buildTenantTree = (groups) => {
    const map = {};
    const roots = [];

    groups.forEach((dept) => {
      map[dept.id] = { ...dept, children: [] };
    });

    groups.forEach((group) => {
      if (group.parent === null) {
        roots.push(map[group.id]);
      } else {
        if (map[group.parent]) {
          map[group.parent].children.push(map[group.id]);
        }
      }
    });

    return roots;
  };

  const BroadcastGroupTree = ({ depts }) => {
    const [expandedNodes, setExpandedNodes] = useState({});

    const toggleExpand = (id) => {
      setExpandedNodes((prevState) => ({
        ...prevState,
        [id]: !prevState[id],
      }));
    };

    const displayTree = (nodes) => {
      return (
        <ul>
          {nodes.map((node) => (
            <li key={node.id} className="node-item">
              <div className="flex justify-between items-center">
                {node.children.length !== 0 ? (
                  <div
                    className="flex justify-normal items-center"
                    onClick={() => toggleExpand(node.id)}
                  >
                    {expandedNodes[node.id] ? (
                      <MinusCircleIcon className="mr-5 w-5 h-5 text-mayo-link-text hover:opacity-50" />
                    ) : (
                      <PlusCircleIcon className="mr-5 w-5 h-5 text-mayo-link-text hover:opacity-50" />
                    )}
                    <div className="underline hover:opacity-50 cursor-pointer text-mayo-light-gray text-2xl">
                      [{node.code || "None"}] {node.name}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-normal items-center">
                    <div className="text-xl text-mayo-light-gray">
                      [{node.code || "None"}] {node.name}
                    </div>
                  </div>
                )}
                {node.id !== nodeId ? (
                  <button
                    className="button-small-white ml-2 items-end"
                    onClick={() => {
                      returnId(nodeId, node.id);
                    }}
                  >
                    選択
                  </button>
                ) : (
                  <div className="p-2 border rounded-xl bg-slate-400 text-white ">
                    選択中のグループ
                  </div>
                )}
              </div>
              <div className="divider mt-2"></div>
              {node.children &&
                node.children.length > 0 &&
                expandedNodes[node.id] && (
                  <div className="ml-4 mt-2">{displayTree(node.children)}</div>
                )}
            </li>
          ))}
        </ul>
      );
    };

    const treeData = buildTenantTree(depts);

    return <div>{displayTree(treeData)}</div>;
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center backdrop-brightness-50 z-[1000]">
      <div className="bg-white border border-text-field-border rounded-lg w-1/2 max-h-1/2 overflow-y-auto">
        <div className="mayo-card-header p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">新しいグループの親を選択</h2>
          <button onClick={onRequestClose} className="text-xl hover:opacity-50">
            <svg
              className="ml-2 fill-body hover:fill-primary"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <XCircleIcon />
            </svg>
          </button>
        </div>
        {loading ? (
          <div className="mt-10">
            <LoadingIcon />
          </div>
        ) : broadcastGroups.length > 0 ? (
          <div className="mayo-card-body-modal overflow-y-auto max-h-[60vh] p-4">
            <BroadcastGroupTree depts={broadcastGroups} />
          </div>
        ) : (
          <p className="flex items-center justify-center mt-5">
            一斉発信選択がありません
          </p>
        )}
        <div className="flex justify-end gap-4 mb-5 mt-5 mr-4">
          <button onClick={onRequestClose} className="button-small-white">
            {"キャンセル"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeBroadcastGroupParentModal;
